"use client";

import clsx from "clsx";
import {
  type ComponentProps,
  type CSSProperties,
  useCallback,
  useState,
} from "react";

import { getResponsiveImage } from "@shared/utils/image-srcset";

import s from "./image.module.scss";

export type ImageProps = Omit<ComponentProps<"img">, "ref"> & {
  src: string;
  alt: string;
  objectFit?: CSSProperties["objectFit"];
  preloaded?: boolean;
  loading?: "eager" | "lazy";
  className?: string;
  style?: CSSProperties;
  ref?: React.Ref<HTMLDivElement>;
  optimize?: boolean;
  quality?: number;
  format?: "webp" | "avif" | "jpeg" | "png";
  /** Для изображения первого экрана: грузится сразу и с высоким приоритетом */
  priority?: boolean;
};

export const Image = (props: ImageProps) => {
  const {
    className,
    style,
    sizes = "100vw",
    width,
    height,
    objectFit,
    loading = "lazy",
    preloaded = true,
    ref,
    src,
    optimize = true,
    quality,
    format = "webp",
    priority = false,
    ...restProps
  } = props;

  const [isLoading, setIsLoading] = useState(true);

  const handleLoad = () => {
    setIsLoading(false);
  };

  /**
   * Картинка из кэша браузера бывает уже загружена к моменту гидрации —
   * события load не будет, и без этой проверки обёртка навсегда осталась бы
   * в data-loading="true", то есть картинка была бы скрыта
   */
  const handleImageRef = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete && node.naturalWidth > 0) {
      setIsLoading(false);
    }
  }, []);

  const responsive = optimize
    ? getResponsiveImage(src, format, quality)
    : { src, srcSet: undefined };

  return (
    <div className={clsx(s.root, className)} data-loading={isLoading} ref={ref}>
      <img
        data-preloaded={preloaded}
        className={s.image}
        ref={handleImageRef}
        onLoad={handleLoad}
        sizes={sizes}
        loading={priority ? "eager" : loading}
        fetchPriority={priority ? "high" : undefined}
        decoding={priority ? "sync" : "async"}
        src={responsive.src}
        srcSet={responsive.srcSet}
        style={{
          ...(width ? { "--image-width": width } : {}),
          ...(height ? { "--image-height": height } : {}),
          ...(objectFit ? { "--object-fit": objectFit } : {}),
          ...style,
        }}
        {...restProps}
      />
    </div>
  );
};

Image.displayName = "Image";
